import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';

const gcd = (a, b) => { a = Math.abs(a); b = Math.abs(b); while (b) { [a, b] = [b, a % b]; } return a || 1; };
const simplify = (n, d) => { const g = gcd(n, d); return [n/g, d/g]; };

function PieFrac({ n, d, color = '#1e3a8a', size = 100 }) {
  if (!d || d === 0) return null;
  const cx = size/2, cy = size/2, r = size/2 - 4;
  // Simplify visual to same denom pieces
  const pct = Math.max(0, Math.min(1, n/d));
  const angle = pct * 2 * Math.PI;
  const largeArc = angle > Math.PI ? 1 : 0;
  const x = cx + r * Math.sin(angle);
  const y = cy - r * Math.cos(angle);
  // Draw sector paths for each unit slice
  const slices = [];
  for (let i = 0; i < d; i++) {
    const a0 = (i/d) * 2 * Math.PI;
    const a1 = ((i+1)/d) * 2 * Math.PI;
    const x0 = cx + r * Math.sin(a0), y0 = cy - r * Math.cos(a0);
    const x1 = cx + r * Math.sin(a1), y1 = cy - r * Math.cos(a1);
    const large = (a1 - a0) > Math.PI ? 1 : 0;
    slices.push(
      <path key={i} d={`M${cx} ${cy} L${x0} ${y0} A${r} ${r} 0 ${large} 1 ${x1} ${y1} Z`}
        fill={i < n ? color : '#e5e7eb'} stroke="#ffffff" strokeWidth="1.5" />
    );
  }
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
      {slices}
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth="1.5" />
    </svg>
  );
}

function FracDisplay({ n, d, size = 'lg' }) {
  const cls = size === 'lg' ? 'text-3xl' : size === 'md' ? 'text-2xl' : 'text-xl';
  return (
    <span className={`inline-flex flex-col items-center align-middle mx-1 ${cls} font-semibold`}>
      <span className="leading-none">{n}</span>
      <span className="w-full border-t-2 border-current my-0.5"></span>
      <span className="leading-none">{d}</span>
    </span>
  );
}

function FInput({ n, d, setN, setD }) {
  return (
    <div className="flex flex-col items-center">
      <input type="number" value={n} onChange={e => setN(+e.target.value || 0)} className="w-20 h-11 text-center text-xl font-semibold border border-slate-200 rounded-lg bg-slate-50" />
      <div className="w-20 border-t-2 border-slate-400 my-1"></div>
      <input type="number" value={d} onChange={e => setD(+e.target.value || 0)} className="w-20 h-11 text-center text-xl font-semibold border border-slate-200 rounded-lg bg-slate-50" />
    </div>
  );
}

export default function FractionCalc() {
  const [n1, setN1] = useState(2); const [d1, setD1] = useState(7);
  const [op, setOp] = useState('+');
  const [n2, setN2] = useState(3); const [d2, setD2] = useState(8);
  const [showExpl, setShowExpl] = useState(false);

  const lcmFn = (a, b) => Math.abs(a * b) / gcd(a, b);

  const c = useMemo(() => {
    if (!d1 || !d2) return null;
    let n, d;
    if (op === '+') { n = n1*d2 + n2*d1; d = d1*d2; }
    else if (op === '−') { n = n1*d2 - n2*d1; d = d1*d2; }
    else if (op === '×') { n = n1*n2; d = d1*d2; }
    else { n = n1*d2; d = d1*n2; }
    if (d < 0) { n = -n; d = -d; }
    const [sn, sd] = simplify(n, d);
    return { n, d, sn, sd, decimal: n/d, commonDen: d1*d2, lhs: n1*d2, rhs: n2*d1, lhsSum: n1*n2, rhsSum: d1*d2, lhsDiv: n1*d2, rhsDiv: n2*d1 };
  }, [n1, d1, n2, d2, op]);

  return (
    <CalcShell look="cnet" title="Fraction Calculator" subtitle="Add, subtract, multiply and divide fractions. Includes pie-chart visuals and full step-by-step working.">
      {/* Input Row */}
      <div className="flex items-center justify-center gap-3 flex-wrap py-2">
        <FInput n={n1} d={d1} setN={setN1} setD={setD1} />
        <select value={op} onChange={e => setOp(e.target.value)} className="h-24 w-14 text-2xl font-bold border border-slate-200 rounded-xl bg-slate-50 text-center">
          <option>+</option><option>−</option><option>×</option><option>÷</option>
        </select>
        <FInput n={n2} d={d2} setN={setN2} setD={setD2} />
        <div className="text-2xl font-bold">=</div>
        {c && <div className="text-3xl font-bold text-blue-700"><FracDisplay n={c.sn} d={c.sd} /></div>}
      </div>

      {/* Pie chart visualization */}
      {c && (
        <div className="flex items-center justify-center gap-4 flex-wrap py-6 bg-slate-50 rounded-2xl mt-4">
          <PieFrac n={n1} d={d1} />
          <span className="text-3xl font-bold text-slate-700">{op}</span>
          <PieFrac n={n2} d={d2} />
          <span className="text-3xl font-bold text-slate-700">=</span>
          <PieFrac n={c.sn} d={c.sd} />
        </div>
      )}

      {c && (
        <div className="mt-6">
          <table className="data-table">
            <tbody>
              <tr><td>Unsimplified</td><td className="text-right">{c.n} / {c.d}</td></tr>
              <tr><td>Simplified</td><td className="text-right font-semibold">{c.sn} / {c.sd}</td></tr>
              <tr><td>Decimal</td><td className="text-right">{c.decimal.toLocaleString(undefined, { maximumFractionDigits: 12 })}</td></tr>
            </tbody>
          </table>

          {/* Calculation Steps */}
          <div className="mt-6">
            <h3 className="text-lg font-bold text-slate-900 mb-3">Calculation steps</h3>
            <div className="space-y-3 text-slate-800">
              {(op === '+' || op === '−') && (
                <>
                  <div className="flex items-center gap-2 text-xl">
                    <FracDisplay n={n1} d={d1} /> <span>{op}</span> <FracDisplay n={n2} d={d2} />
                  </div>
                  <div className="flex items-center gap-2 text-xl">
                    <span className="font-bold">=</span>
                    <FracDisplay n={`${n1} × ${d2}`} d={`${d1} × ${d2}`} /> <span>{op}</span> <FracDisplay n={`${n2} × ${d1}`} d={`${d2} × ${d1}`} />
                  </div>
                  <div className="flex items-center gap-2 text-xl">
                    <span className="font-bold">=</span>
                    <FracDisplay n={n1*d2} d={c.commonDen} /> <span>{op}</span> <FracDisplay n={n2*d1} d={c.commonDen} />
                  </div>
                  <div className="flex items-center gap-2 text-xl">
                    <span className="font-bold">=</span>
                    <FracDisplay n={`${n1*d2} ${op} ${n2*d1}`} d={c.commonDen} />
                  </div>
                  <div className="flex items-center gap-2 text-xl">
                    <span className="font-bold">=</span>
                    <FracDisplay n={c.n} d={c.d} />
                  </div>
                  {(c.n !== c.sn || c.d !== c.sd) && (
                    <div className="flex items-center gap-2 text-xl">
                      <span className="font-bold">=</span>
                      <FracDisplay n={c.sn} d={c.sd} />
                      <span className="text-sm text-slate-500 ml-2">(simplified by GCD {gcd(c.n, c.d)})</span>
                    </div>
                  )}
                </>
              )}
              {op === '×' && (
                <>
                  <div className="flex items-center gap-2 text-xl">
                    <FracDisplay n={n1} d={d1} /> <span>×</span> <FracDisplay n={n2} d={d2} />
                  </div>
                  <div className="flex items-center gap-2 text-xl">
                    <span className="font-bold">=</span>
                    <FracDisplay n={`${n1} × ${n2}`} d={`${d1} × ${d2}`} />
                  </div>
                  <div className="flex items-center gap-2 text-xl">
                    <span className="font-bold">=</span>
                    <FracDisplay n={c.n} d={c.d} />
                  </div>
                  {(c.n !== c.sn || c.d !== c.sd) && (
                    <div className="flex items-center gap-2 text-xl">
                      <span className="font-bold">=</span>
                      <FracDisplay n={c.sn} d={c.sd} />
                      <span className="text-sm text-slate-500 ml-2">(simplified)</span>
                    </div>
                  )}
                </>
              )}
              {op === '÷' && (
                <>
                  <div className="flex items-center gap-2 text-xl">
                    <FracDisplay n={n1} d={d1} /> <span>÷</span> <FracDisplay n={n2} d={d2} />
                  </div>
                  <div className="flex items-center gap-2 text-xl">
                    <span className="font-bold">=</span>
                    <FracDisplay n={n1} d={d1} /> <span>×</span> <FracDisplay n={d2} d={n2} />
                    <span className="text-sm text-slate-500 ml-2">(multiply by reciprocal)</span>
                  </div>
                  <div className="flex items-center gap-2 text-xl">
                    <span className="font-bold">=</span>
                    <FracDisplay n={`${n1} × ${d2}`} d={`${d1} × ${n2}`} />
                  </div>
                  <div className="flex items-center gap-2 text-xl">
                    <span className="font-bold">=</span>
                    <FracDisplay n={c.n} d={c.d} />
                  </div>
                  {(c.n !== c.sn || c.d !== c.sd) && (
                    <div className="flex items-center gap-2 text-xl">
                      <span className="font-bold">=</span>
                      <FracDisplay n={c.sn} d={c.sd} />
                      <span className="text-sm text-slate-500 ml-2">(simplified)</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Further Explanation Toggle */}
          <div className="mt-6">
            <button
              onClick={() => setShowExpl(v => !v)}
              data-testid="frac-further-toggle"
              className="text-sm px-4 py-1.5 rounded bg-slate-200 hover:bg-slate-300 text-slate-800 font-medium"
            >
              {showExpl ? '− Hide further explanation' : '+ Show further explanation'}
            </button>
            {showExpl && (
              <div className="mt-4 p-5 bg-blue-50 border border-blue-100 rounded-xl" data-testid="frac-further-panel">
                <h3 className="text-lg font-bold text-slate-900 mb-3">Further explanation</h3>
                <div className="space-y-3 text-slate-800 text-[15px] leading-7">
                  <div>For the problem:
                    <div className="flex items-center gap-2 mt-2 text-xl">
                      <FracDisplay n={n1} d={d1} /> <span>{op}</span> <FracDisplay n={n2} d={d2} /> <span>= ?</span>
                    </div>
                  </div>

                  {(op === '+' || op === '−') && (
                    <>
                      <div>
                        The <a className="text-blue-600 underline">Least Common Multiple (LCM) of {d1} and {d2}</a> is <b>{lcmFn(d1, d2)}</b>.
                        Multiply the numerator and denominator of each fraction by whatever value will result in the denominator of each fraction being equal to the LCM:
                      </div>
                      <div className="flex items-center gap-2 text-xl">
                        <FracDisplay n={n1} d={d1} /> <span>{op}</span> <FracDisplay n={n2} d={d2} /> <span>=</span>
                        <FracDisplay n={`${n1} × ${lcmFn(d1, d2) / d1}`} d={`${d1} × ${lcmFn(d1, d2) / d1}`} />
                        <span>{op}</span>
                        <FracDisplay n={`${n2} × ${lcmFn(d1, d2) / d2}`} d={`${d2} × ${lcmFn(d1, d2) / d2}`} />
                        <span>=</span>
                        <FracDisplay n={n1 * (lcmFn(d1, d2) / d1)} d={lcmFn(d1, d2)} />
                        <span>{op}</span>
                        <FracDisplay n={n2 * (lcmFn(d1, d2) / d2)} d={lcmFn(d1, d2)} />
                      </div>
                      <div>Now that the fractions have like denominators, {op === '+' ? 'add' : 'subtract'} the numerators:</div>
                      <div className="flex items-center gap-2 text-xl">
                        <FracDisplay n={n1 * (lcmFn(d1, d2) / d1)} d={lcmFn(d1, d2)} />
                        <span>{op}</span>
                        <FracDisplay n={n2 * (lcmFn(d1, d2) / d2)} d={lcmFn(d1, d2)} />
                        <span>=</span>
                        <FracDisplay n={`${n1 * (lcmFn(d1, d2) / d1)} ${op} ${n2 * (lcmFn(d1, d2) / d2)}`} d={lcmFn(d1, d2)} />
                        <span>=</span>
                        <FracDisplay n={c.n} d={c.d} />
                      </div>
                    </>
                  )}
                  {op === '×' && (
                    <div>Multiply numerators together and denominators together:
                      <div className="flex items-center gap-2 mt-2 text-xl">
                        <FracDisplay n={n1} d={d1} /> <span>×</span> <FracDisplay n={n2} d={d2} /> <span>=</span>
                        <FracDisplay n={`${n1} × ${n2}`} d={`${d1} × ${d2}`} /> <span>=</span>
                        <FracDisplay n={c.n} d={c.d} />
                      </div>
                    </div>
                  )}
                  {op === '÷' && (
                    <div>Dividing by a fraction is the same as multiplying by its reciprocal:
                      <div className="flex items-center gap-2 mt-2 text-xl">
                        <FracDisplay n={n1} d={d1} /> <span>÷</span> <FracDisplay n={n2} d={d2} /> <span>=</span>
                        <FracDisplay n={n1} d={d1} /> <span>×</span> <FracDisplay n={d2} d={n2} /> <span>=</span>
                        <FracDisplay n={c.n} d={c.d} />
                      </div>
                    </div>
                  )}

                  <div>The result is:
                    <div className="flex items-center gap-2 mt-2 text-xl">
                      <FracDisplay n={n1} d={d1} /> <span>{op}</span> <FracDisplay n={n2} d={d2} /> <span>=</span>
                      <FracDisplay n={c.sn} d={c.sd} />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </CalcShell>
  );
}
